import {
  cloneElement,
  forwardRef,
  PropsWithChildren,
  ReactElement,
  ReactHTMLElement,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
} from 'react'
import { Portal } from 'react-portal'
import { useIsWindowFocused } from '../../hooks'
import { calculatePositionedStyle } from '../../utils/positionUtils'
import { twMerge } from 'tailwind-merge'
import { FadingScrollable } from './FadingScrollable'
import { Interactive, listItemVariants, listSeparator, popupSurface } from '@highlight/ui'

type Position = 'top' | 'bottom' | 'left' | 'right' | 'bottom-right'

export interface MenuItemType {
  divider?: boolean
  label?: string | ReactHTMLElement<HTMLAnchorElement> | ReactElement
  onClick?: (e: React.MouseEvent) => void
  disabled?: boolean
}

export interface ContextMenuProps {
  disabled?: boolean
  items: MenuItemType[] | ReactElement
  leftClick?: boolean
  offset?: number
  position: Position
  triggerId: string
  wrapperClassName?: string
  menuStyle?: React.CSSProperties
  menuItemStyle?: React.CSSProperties
  menuClassName?: string
  onOpen?: () => void
  onClose?: () => void
  open?: boolean
  targetElementId?: string
  onTrigger?: (e: Event) => void
  style?: React.CSSProperties
}

// Add this tiny helper up top (outside the component)
function pathContains(path: EventTarget[], el?: Node | null) {
  if (!el) return false
  return path.includes(el)
}

export const ContextMenu = forwardRef<HTMLDivElement, PropsWithChildren<ContextMenuProps>>(
  (
    {
      children,
      disabled,
      triggerId,
      position,
      leftClick,
      items,
      offset = 0,
      wrapperClassName,
      menuStyle,
      menuItemStyle,
      menuClassName,
      onOpen,
      onClose,
      open,
      targetElementId,
      onTrigger,
      ...props
    },
    ref,
  ) => {
    const isWindowFocused = useIsWindowFocused()

    const [isOpen, setOpen] = useState(open ?? false)
    const [appliedStyle, setAppliedStyle] = useState<React.CSSProperties>({})
    const containerRef = useRef<HTMLDivElement>(null)
    const elemRef = useRef<HTMLDivElement>(null)

    // Expose the containerRef to the parent component
    useImperativeHandle(ref, () => containerRef.current!, [])

    useEffect(() => {
      if (!isWindowFocused) {
        setOpen(false)
      }
    }, [isWindowFocused])

    useEffect(() => {
      if (isOpen && typeof onOpen === 'function') {
        onOpen()
      } else if (!isOpen && typeof onClose === 'function') {
        onClose()
      }
    }, [isOpen])

    useEffect(() => {
      if (!isOpen) return

      const resizeObserver = new ResizeObserver(() => {
        recalculatePosition()
      })

      if (elemRef.current) {
        resizeObserver.observe(elemRef.current)
      }

      return () => {
        if (elemRef.current) {
          resizeObserver.unobserve(elemRef.current)
        }
        resizeObserver.disconnect()
      }
    }, [isOpen])

    useEffect(() => {
      if (open !== undefined && open !== isOpen) {
        setOpen(open)
      }
    }, [open])

    function recalculatePosition() {
      const targetPositionedElement = targetElementId ? document.getElementById(targetElementId) : undefined
      const target = targetPositionedElement ?? containerRef.current
      if (!elemRef.current || !target) {
        return
      }
      const styles = calculatePositionedStyle(target, elemRef.current, position, offset, !!targetElementId)
      setAppliedStyle(styles)
    }

    // Keep references you want to be considered "inside":
    const triggerEl = () => document.getElementById(triggerId)

    useEffect(() => {
      if (disabled) return

      // ——— Trigger handler ———
      const onMenuTrigger = (evt: Event) => {
        if (typeof onTrigger === 'function') onTrigger(evt)

        const tEl = triggerEl()
        if (!tEl) return

        const path = (evt.composedPath?.() ?? []) as EventTarget[]
        const isInsideTrigger = pathContains(path, tEl)

        // If we’re opening via right click, prevent the native menu
        if (!leftClick && evt.type === 'contextmenu') {
          ;(evt as MouseEvent).preventDefault()
        }

        // Toggle only when the trigger was actually hit
        if (isInsideTrigger) {
          setOpen((prev) => !prev)
        }
      }

      const trigger = triggerEl() || document
      trigger.addEventListener(leftClick ? 'pointerdown' : 'contextmenu', onMenuTrigger, true)

      // ——— Global outside detector (capture-phase, earliest) ———
      const onDocPointerDownCapture = (e: PointerEvent) => {
        if (!isOpen) return

        const path = (e.composedPath?.() ?? []) as EventTarget[]

        const tEl = triggerEl()
        const mEl = elemRef.current
        const cEl = containerRef.current // wrapper that also counts as inside

        const inside = pathContains(path, tEl) || pathContains(path, mEl) || pathContains(path, cEl)

        if (!inside) {
          setOpen(false)
        }
      }

      // Capture-phase so we run before most app handlers and prevent flicker/races.
      document.addEventListener('pointerdown', onDocPointerDownCapture, true)

      return () => {
        trigger.removeEventListener(leftClick ? 'pointerdown' : 'contextmenu', onMenuTrigger, true)
        document.removeEventListener('pointerdown', onDocPointerDownCapture, true)
      }
    }, [isOpen, triggerId, disabled, leftClick, onTrigger])

    useEffect(() => {
      if (isOpen) {
        recalculatePosition()
      }
    }, [isOpen])

    useEffect(() => {
      if (disabled) {
        setOpen(false)
      }
    }, [disabled, isOpen])

    return (
      <div
        {...props}
        ref={containerRef}
        className={twMerge('relative h-fit w-fit [&>svg]:pointer-events-none', wrapperClassName)}
        style={props.style}
      >
        {typeof children === 'function'
          ? // @ts-ignore
            children({ isOpen })
          : children}
        {!disabled && isOpen && (
          <Portal>
            <Interactive>
              {Array.isArray(items) ? (
                <FadingScrollable
                  ref={elemRef}
                  className={twMerge(
                    popupSurface,
                    'relative z-10 flex h-fit max-h-[400px] w-fit min-w-56 flex-col gap-px p-1',
                    menuClassName,
                  )}
                  style={{ ...appliedStyle, ...menuStyle }}
                >
                  {items.map((item, index) => {
                    if (item.divider) {
                      return <div key={`divider-${index}`} className={listSeparator} />
                    }
                    return (
                      <div
                        key={`menu-item-${index}`}
                        className={twMerge(
                          listItemVariants(),
                          item.disabled ? 'text-foreground-disabled' : 'hover:bg-element-hover',
                          typeof item.label === 'object' && item.label.type === 'a' && 'p-0',
                        )}
                        onClick={(e) => {
                          setOpen(false)
                          item?.onClick?.(e)
                          e.stopPropagation()
                        }}
                        style={menuItemStyle}
                      >
                        {typeof item.label === 'object' && item.label.type === 'a'
                          ? cloneElement(item.label as React.ReactElement, {
                              className:
                                'flex w-full items-center gap-2 px-[7px] py-1 text-foreground hover:no-underline',
                            })
                          : item.label}
                      </div>
                    )
                  })}
                </FadingScrollable>
              ) : (
                <div
                  ref={elemRef}
                  className={twMerge(popupSurface, 'relative z-[2] h-fit w-fit overflow-hidden')}
                  style={{ ...appliedStyle, ...menuStyle }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!e.shiftKey) setOpen(false)
                  }}
                >
                  {items}
                </div>
              )}
            </Interactive>
          </Portal>
        )}
      </div>
    )
  },
)
